import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiX,
  FiChevronRight,
  FiPhone,
  FiMail,
  FiMapPin,
  FiChevronDown,
  FiArrowUpRight,
} from "react-icons/fi";
import logo from "../assets/images/logo.png";
import "./Navbar.css";

/* ── Nav link definitions ── */
const navLinks = [
  { label: "Home", path: "/" },
  {
    label: "About",
    path: "/about",
    dropdown: [
      {
        label: "Bakwena Kit History",
        path: "/about",
        desc: "Who we are & our story since 2021",
        icon: "🏢",
      },
      {
        label: "Mission & Vision",
        path: "/mission-vision",
        desc: "Our values, mission and vision",
        icon: "🎯",
      },
    ],
  },
  {
    label: "Services",
    path: "/services",
    dropdown: [
      {
        label: "All Services",
        path: "/services",
        desc: "Browse everything we offer",
        isAll: true,
      },
      {
        label: "Formwork and Scaffolding",
        path: "/services/framework-and-scaffolding",
        desc: "Temporary molds & safe working platforms",
      },
      {
        label: "Columns",
        path: "/services/columns",
        desc: "Vertical structural members & load transfer",
      },
      {
        label: "Retaining Walls",
        path: "/services/retaining-walls",
        desc: "Engineered soil retention structures",
      },
      {
        label: "Suspended Slab",
        path: "/services/suspended-slab",
        desc: "Elevated reinforced concrete floors",
      },
      {
        label: "Ring Beam",
        path: "/services/ring-beam",
        desc: "Continuous horizontal load distribution",
      },
      {
        label: "Transport",
        path: "/services/transport",
        desc: "Site movement planning & infrastructure",
      },
      {
        label: "Temporary Work Design",
        path: "/services/temporary-work-design",
        desc: "Structural stability during construction",
      },
      {
        label: "PR Engineer Services",
        path: "/services/pr-engineer-services",
        desc: "ECSA-registered design & project leadership",
      },
    ],
  },
  {
    label: "Portfolio",
    path: "/projects",
    dropdown: [
      {
        label: "Projects",
        path: "/portfolio",
        desc: "Browse our completed project gallery",
        icon: "🏗",
      },
      {
        label: "Products",
        path: "/products",
        desc: "Formwork, shoring & decking systems",
        icon: "🔩",
      },
      {
        label: "Project Videos",
        path: "/project-videos",
        desc: "Watch our work in motion — 43 videos",
        icon: "🎬",
      },
    ],
  },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled,             setScrolled]             = useState(false);
  const [menuOpen,             setMenuOpen]             = useState(false);
  const [openDrop,             setOpenDrop]             = useState(null);
  const [mobileAboutOpen,      setMobileAboutOpen]      = useState(false);
  const [mobileServicesOpen,   setMobileServicesOpen]   = useState(false);
  const [mobilePortfolioOpen,  setMobilePortfolioOpen]  = useState(false);

  const location      = useLocation();
  const aboutRef      = useRef(null);
  const servicesRef   = useRef(null);
  const portRef       = useRef(null);
  const aboutTimer    = useRef(null);
  const servicesTimer = useRef(null);
  const portTimer     = useRef(null);

  /* ── scroll detection ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── reset on route change ── */
  useEffect(() => {
    setMenuOpen(false);
    setOpenDrop(null);
    setMobileAboutOpen(false);
    setMobileServicesOpen(false);
    setMobilePortfolioOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /* ── body scroll lock ── */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  /* ── close dropdowns on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      const clickedAbout    = aboutRef.current    && aboutRef.current.contains(e.target);
      const clickedServices = servicesRef.current && servicesRef.current.contains(e.target);
      const clickedPort     = portRef.current     && portRef.current.contains(e.target);
      if (!clickedAbout && !clickedServices && !clickedPort) setOpenDrop(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* helpers for hover intent */
  const makeEnter = (key) => () => {
    if (key === "about")     clearTimeout(aboutTimer.current);
    if (key === "services")  clearTimeout(servicesTimer.current);
    if (key === "portfolio") clearTimeout(portTimer.current);
    setOpenDrop(key);
  };

  const makeLeave = (key) => () => {
    if (key === "about") {
      aboutTimer.current = setTimeout(
        () => setOpenDrop(d => d === "about" ? null : d), 180
      );
    } else if (key === "services") {
      servicesTimer.current = setTimeout(
        () => setOpenDrop(d => d === "services" ? null : d), 180
      );
    } else {
      portTimer.current = setTimeout(
        () => setOpenDrop(d => d === "portfolio" ? null : d), 180
      );
    }
  };

  const isAboutActive     = ["/about", "/mission-vision"].includes(location.pathname);
  const isServicesActive  = location.pathname.startsWith("/services");
  const isPortfolioActive = ["/projects", "/portfolio", "/products", "/project-videos"].includes(location.pathname);

  return (
    <>
      <div className="header-wrapper">

        {/* ── Top Bar ── */}
        <div className="topbar">
          <div className="topbar__inner">
            <div className="topbar__slogan">
              ⚡ We go through all construction stages
            </div>
            <div className="topbar__contact">
              <a
                href="https://maps.google.com/?q=52+Jakaranda+Street+Hennopspark+Centurion+0157"
                target="_blank"
                rel="noopener noreferrer"
                className="topbar__item"
              >
                <span className="topbar__icon-circle"><FiMapPin size={12} /></span>
                52 Jakaranda Street, Hennopspark, Centurion, 0157
              </a>
              <span className="topbar__divider">|</span>
              <a href="mailto:sales@bakwenakit.co.za" className="topbar__item">
                <span className="topbar__icon-circle"><FiMail size={12} /></span>
                sales@bakwenakit.co.za
              </a>
              <span className="topbar__divider">|</span>
              <a href="tel:+27120041000" className="topbar__item">
                <span className="topbar__icon-circle"><FiPhone size={12} /></span>
                012 004-1000
              </a>
            </div>
          </div>
        </div>

        {/* ── Main Navbar ── */}
        <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}>
          <div className="navbar__inner">
            <Link to="/" className="navbar__logo">
              <img src={logo} alt="Bakwena Kit" className="navbar__logo-image" />
            </Link>

            <ul className="navbar__links">
              {navLinks.map((link) => {

                /* ── ABOUT dropdown ── */
                if (link.label === "About") {
                  return (
                    <li
                      key={link.path}
                      className="navbar__item--has-drop"
                      ref={aboutRef}
                      onMouseEnter={makeEnter("about")}
                      onMouseLeave={makeLeave("about")}
                    >
                      <button
                        className={`navbar__link navbar__link--drop-trigger${isAboutActive ? " navbar__link--active" : ""}`}
                        onClick={() => setOpenDrop(d => d === "about" ? null : "about")}
                        aria-expanded={openDrop === "about"}
                        type="button"
                      >
                        {link.label}
                        <FiChevronDown
                          size={13}
                          className={`navbar__drop-chevron${openDrop === "about" ? " navbar__drop-chevron--open" : ""}`}
                        />
                      </button>

                      <div className={`navbar__dropdown${openDrop === "about" ? " navbar__dropdown--open" : ""}`}>
                        <div className="navbar__drop-inner">
                          <div className="navbar__drop-label-col">
                            <span className="navbar__drop-eyebrow">ABOUT US</span>
                            <h3 className="navbar__drop-heading">Who<br />We Are</h3>
                            <div className="navbar__drop-bar" />
                          </div>
                          <div className="navbar__drop-links">
                            {link.dropdown.map((item, i) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`navbar__drop-link${location.pathname === item.path ? " navbar__drop-link--active" : ""}`}
                                style={{ animationDelay: `${i * 0.05}s` }}
                              >
                                <div className="navbar__drop-link-inner">
                                  <div className="navbar__drop-link-text">
                                    <span className="navbar__drop-link-label">{item.label}</span>
                                    <span className="navbar__drop-link-desc">{item.desc}</span>
                                  </div>
                                  <FiArrowUpRight size={18} className="navbar__drop-link-icon" />
                                </div>
                                <div className="navbar__drop-link-line" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }

                /* ── SERVICES dropdown ── */
                if (link.label === "Services") {
                  const allServicesItem = link.dropdown.find(item => item.isAll);
                  const serviceItems    = link.dropdown.filter(item => !item.isAll);

                  return (
                    <li
                      key={link.path}
                      className="navbar__item--has-drop"
                      ref={servicesRef}
                      onMouseEnter={makeEnter("services")}
                      onMouseLeave={makeLeave("services")}
                    >
                      <button
                        className={`navbar__link navbar__link--drop-trigger${isServicesActive ? " navbar__link--active" : ""}`}
                        onClick={() => setOpenDrop(d => d === "services" ? null : "services")}
                        aria-expanded={openDrop === "services"}
                        type="button"
                      >
                        {link.label}
                        <FiChevronDown
                          size={13}
                          className={`navbar__drop-chevron${openDrop === "services" ? " navbar__drop-chevron--open" : ""}`}
                        />
                      </button>

                      <div className={`navbar__dropdown navbar__dropdown--services${openDrop === "services" ? " navbar__dropdown--open" : ""}`}>
                        <div className="navbar__drop-inner navbar__drop-inner--services">
                          <div className="navbar__drop-label-col">
                            <span className="navbar__drop-eyebrow">WHAT WE DO</span>
                            <h3 className="navbar__drop-heading">Our<br />Services</h3>
                            <div className="navbar__drop-bar" />
                          </div>
                          <div className="navbar__drop-links-wrapper">
                            {allServicesItem && (
                              <Link
                                to={allServicesItem.path}
                                className={`navbar__drop-all-services${location.pathname === allServicesItem.path ? " navbar__drop-all-services--active" : ""}`}
                              >
                                <div className="navbar__drop-all-services-text">
                                  <span className="navbar__drop-all-services-label">{allServicesItem.label}</span>
                                  <span className="navbar__drop-all-services-desc">{allServicesItem.desc}</span>
                                </div>
                                <FiArrowUpRight size={16} className="navbar__drop-all-services-icon" />
                              </Link>
                            )}
                            <div className="navbar__drop-links navbar__drop-links--grid">
                              {serviceItems.map((item, i) => (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  className={`navbar__drop-link navbar__drop-link--compact${location.pathname === item.path ? " navbar__drop-link--active" : ""}`}
                                  style={{ animationDelay: `${i * 0.04}s` }}
                                >
                                  <div className="navbar__drop-link-inner">
                                    <div className="navbar__drop-link-text">
                                      <span className="navbar__drop-link-label">{item.label}</span>
                                      <span className="navbar__drop-link-desc">{item.desc}</span>
                                    </div>
                                    <FiArrowUpRight size={16} className="navbar__drop-link-icon" />
                                  </div>
                                  <div className="navbar__drop-link-line" />
                                </Link>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }

                /* ── PORTFOLIO dropdown ── */
                if (link.label === "Portfolio") {
                  return (
                    <li
                      key={link.path}
                      className="navbar__item--has-drop"
                      ref={portRef}
                      onMouseEnter={makeEnter("portfolio")}
                      onMouseLeave={makeLeave("portfolio")}
                    >
                      <button
                        className={`navbar__link navbar__link--drop-trigger${isPortfolioActive ? " navbar__link--active" : ""}`}
                        onClick={() => setOpenDrop(d => d === "portfolio" ? null : "portfolio")}
                        aria-expanded={openDrop === "portfolio"}
                        type="button"
                      >
                        {link.label}
                        <FiChevronDown
                          size={13}
                          className={`navbar__drop-chevron${openDrop === "portfolio" ? " navbar__drop-chevron--open" : ""}`}
                        />
                      </button>

                      <div className={`navbar__dropdown${openDrop === "portfolio" ? " navbar__dropdown--open" : ""}`}>
                        <div className="navbar__drop-inner">
                          <div className="navbar__drop-label-col">
                            <span className="navbar__drop-eyebrow">PROJECTS</span>
                            <h3 className="navbar__drop-heading">Explore<br />Our Work</h3>
                            <div className="navbar__drop-bar" />
                          </div>
                          <div className="navbar__drop-links">
                            {link.dropdown.map((item, i) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className={`navbar__drop-link${location.pathname === item.path ? " navbar__drop-link--active" : ""}`}
                                style={{ animationDelay: `${i * 0.05}s` }}
                              >
                                <div className="navbar__drop-link-inner">
                                  <div className="navbar__drop-link-text">
                                    <span className="navbar__drop-link-label">{item.label}</span>
                                    <span className="navbar__drop-link-desc">{item.desc}</span>
                                  </div>
                                  <FiArrowUpRight size={18} className="navbar__drop-link-icon" />
                                </div>
                                <div className="navbar__drop-link-line" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }

                /* ── plain link ── */
                return (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className={`navbar__link${location.pathname === link.path ? " navbar__link--active" : ""}`}
                    >
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <Link to="/contact" className="navbar__cta">GET A QUOTE</Link>

            <button
              className="navbar__hamburger"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
            </button>
          </div>
        </nav>
      </div>

      {/* ── Mobile Menu ── */}
      <div className={`mobile-menu ${menuOpen ? "mobile-menu--open" : ""}`}>
        <ul className="mobile-menu__links">
          {navLinks.map((link, i) => {

            if (link.label === "About") {
              return (
                <li key={link.path} style={{ animationDelay: `${i * 0.05}s` }}>
                  <button
                    className={`mobile-menu__link mobile-menu__link--has-drop${isAboutActive ? " mobile-menu__link--active" : ""}`}
                    onClick={() => setMobileAboutOpen(v => !v)}
                    type="button"
                  >
                    <FiChevronRight size={18} />
                    {link.label}
                    <FiChevronDown
                      size={14}
                      className={`mobile-drop-chevron${mobileAboutOpen ? " mobile-drop-chevron--open" : ""}`}
                      style={{ marginLeft: "auto" }}
                    />
                  </button>
                  {mobileAboutOpen && (
                    <ul className="mobile-menu__submenu">
                      {link.dropdown.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`mobile-menu__sublink${location.pathname === item.path ? " mobile-menu__sublink--active" : ""}`}
                          >
                            <FiArrowUpRight size={14} />
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            if (link.label === "Services") {
              return (
                <li key={link.path} style={{ animationDelay: `${i * 0.05}s` }}>
                  <button
                    className={`mobile-menu__link mobile-menu__link--has-drop${isServicesActive ? " mobile-menu__link--active" : ""}`}
                    onClick={() => setMobileServicesOpen(v => !v)}
                    type="button"
                  >
                    <FiChevronRight size={18} />
                    {link.label}
                    <FiChevronDown
                      size={14}
                      className={`mobile-drop-chevron${mobileServicesOpen ? " mobile-drop-chevron--open" : ""}`}
                      style={{ marginLeft: "auto" }}
                    />
                  </button>
                  {mobileServicesOpen && (
                    <ul className="mobile-menu__submenu">
                      {link.dropdown.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`mobile-menu__sublink${item.isAll ? " mobile-menu__sublink--all" : ""}${location.pathname === item.path ? " mobile-menu__sublink--active" : ""}`}
                          >
                            <FiArrowUpRight size={14} />
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            if (link.label === "Portfolio") {
              return (
                <li key={link.path} style={{ animationDelay: `${i * 0.05}s` }}>
                  <button
                    className={`mobile-menu__link mobile-menu__link--has-drop${isPortfolioActive ? " mobile-menu__link--active" : ""}`}
                    onClick={() => setMobilePortfolioOpen(v => !v)}
                    type="button"
                  >
                    <FiChevronRight size={18} />
                    {link.label}
                    <FiChevronDown
                      size={14}
                      className={`mobile-drop-chevron${mobilePortfolioOpen ? " mobile-drop-chevron--open" : ""}`}
                      style={{ marginLeft: "auto" }}
                    />
                  </button>
                  {mobilePortfolioOpen && (
                    <ul className="mobile-menu__submenu">
                      {link.dropdown.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`mobile-menu__sublink${location.pathname === item.path ? " mobile-menu__sublink--active" : ""}`}
                          >
                            <FiArrowUpRight size={14} />
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            }

            return (
              <li key={link.path} style={{ animationDelay: `${i * 0.05}s` }}>
                <Link
                  to={link.path}
                  className={`mobile-menu__link${location.pathname === link.path ? " mobile-menu__link--active" : ""}`}
                >
                  <FiChevronRight size={18} />
                  {link.label}
                </Link>
              </li>
            );
          })}
        </ul>

        <Link to="/contact" className="btn-primary mobile-menu__btn" onClick={() => setMenuOpen(false)}>
          Get a Quote
        </Link>

        <div className="mobile-menu__footer">
          <p>© 2025 Bakwena Kit (Pty) Ltd</p>
          <p> B-BBEE Level 1</p>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-overlay" onClick={() => setMenuOpen(false)} />
      )}
    </>
  );
}